import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type Props = { children: ReactNode }

type State = { hasError: boolean; error: Error | null }

function ErrorFallback({ error, onReload }: { error: Error | null; onReload: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F0F4FF] p-8">
      <div className="w-full max-w-2xl rounded-3xl border border-[#1A56FF]/15 bg-white p-8 shadow-xl">
        <h2 className="mb-4 font-[Outfit,sans-serif] text-xl font-bold text-[#0D1B3E]">
          {t('errorBoundary.title')}
        </h2>
        <pre className="mb-6 max-h-64 overflow-auto rounded-xl bg-slate-50 p-4 text-xs text-slate-600 whitespace-pre-wrap">
          {error?.stack ?? error?.message}
        </pre>
        <button
          type="button"
          className="rounded-xl bg-[#1A56FF] px-4 py-2 font-medium text-white hover:opacity-90"
          onClick={onReload}
        >
          {t('errorBoundary.reload')}
        </button>
      </div>
    </div>
  )
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onReload={() => window.location.reload()}
        />
      )
    }
    return this.props.children
  }
}
